import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { PawPrint, Package, Truck, ArrowLeft, Star, Trophy, Storefront } from "@phosphor-icons/react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { saveBrowseContext } from "@/lib/browseContext";
import ListingCard from "@/components/ListingCard";
import { FoundingHat } from "@/components/FoundingHat";

const TIER_META = {
  1: { label: "Tier 1", cls: "" },
  2: { label: "Tier 2", cls: "h57-chip-orange" },
  3: { label: "Tier 3", cls: "h57-chip-gold" },
};

function StarRow({ stars, size = 12 }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} weight={n <= stars ? "fill" : "regular"}
          className={n <= stars ? "text-[#FFD700]" : "text-zinc-700"} />
      ))}
    </div>
  );
}

export default function PublicProfile() {
  const { username } = useParams();
  const [user, setUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [history, setHistory] = useState({ sold_listings: [], feedback: [], sold_count: 0 });
  const [historyTab, setHistoryTab] = useState("sold");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/by-username/${encodeURIComponent(username)}`);
        setUser(data.user);
        const [{ data: L }, { data: H }] = await Promise.all([
          api.get("/listings", { params: { seller_id: data.user.id, status: "active" } }),
          api.get(`/users/${data.user.id}/sold-history`),
        ]);
        setListings(L.listings || []);
        setHistory(H);
        saveBrowseContext((L.listings || []).map((l) => l.id), `seller:${data.user.username}`);
      } catch (err) { toast.error(formatErr(err)); }
      finally { setLoading(false); }
    })();
  }, [username]);

  if (loading) return <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading…</div>;
  if (!user) return <div className="py-24 text-center text-zinc-500">Profile not found.</div>;

  const memberSince = user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—";

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <Link to="/browse" data-testid="profile-back" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest uppercase text-zinc-500 hover:text-white">
        <ArrowLeft size={14} weight="bold" /> Browse
      </Link>

      {/* Header — stripped to only public-safe fields */}
      <header className="mt-6 h57-surface p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-[#0A0A0A] border border-white/10 flex items-center justify-center shrink-0">
            <PawPrint size={40} weight="bold" className="text-zinc-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase inline-flex items-center gap-1" data-testid="profile-handle">
              @{user.username}
              <FoundingHat founding={user.founding_member} size={11} />
            </div>
            <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tighter" data-testid="profile-display-name">
              {user.display_name || user.username}
            </h1>
            <div className="mt-3 flex flex-wrap gap-2">
              {user.founding_member && <span className="h57-chip h57-chip-gold">Founding Member</span>}
              {user.is_vendor && <span className="h57-chip">Verified Seller</span>}
              {user.account_status === "suspended" && (
                <span className="h57-chip !border-red-500/60 !text-red-400">Suspended</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-1 gap-4 md:text-right">
            <div>
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Cards Sold</div>
              <div className="font-display font-black text-2xl" data-testid="profile-sold-count">
                {user.sold_listings_count || 0}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Rating</div>
              <div className="font-display font-black text-2xl flex items-center md:justify-end gap-1" data-testid="profile-rating">
                {user.feedback_avg > 0 ? (
                  <>
                    <Star size={16} weight="fill" className="text-[#FFD700]" />
                    {user.feedback_avg.toFixed(1)}
                    <span className="text-xs text-zinc-500 font-mono">({user.feedback_count})</span>
                  </>
                ) : "—"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Member since</div>
              <div className="font-display font-bold text-sm">{memberSince}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Active listings */}
      <section className="mt-10">
        <h2 className="font-display font-black uppercase text-xl tracking-tight mb-4">
          Active Listings <span className="text-zinc-500">· {listings.length}</span>
        </h2>
        {listings.length === 0 ? (
          <div className="h57-surface p-8 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">
            No active listings
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </section>

      {/* Sold history & feedback (social proof) */}
      {(history.sold_listings.length > 0 || history.feedback.length > 0) && (
        <section className="mt-12" data-testid="profile-history-section">
          <div className="flex items-baseline gap-3 mb-4">
            <Trophy size={18} weight="fill" className="text-[#FFD700]" />
            <h2 className="font-display font-black uppercase text-xl tracking-tight">Track Record</h2>
          </div>

          <div className="flex gap-2 border-b border-white/10 pb-2 mb-4">
            <button
              onClick={() => setHistoryTab("sold")}
              data-testid="history-tab-sold"
              className={`h57-chip !py-2 !px-3 inline-flex items-center gap-1 ${historyTab === "sold" ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}
            >
              <Storefront size={12} weight="bold" />
              Sold <span className="ml-1 font-mono text-[10px] opacity-70">({history.sold_listings.length})</span>
            </button>
            <button
              onClick={() => setHistoryTab("feedback")}
              data-testid="history-tab-feedback"
              className={`h57-chip !py-2 !px-3 inline-flex items-center gap-1 ${historyTab === "feedback" ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}
            >
              <Star size={12} weight="fill" />
              Feedback <span className="ml-1 font-mono text-[10px] opacity-70">({history.feedback.length})</span>
            </button>
          </div>

          {historyTab === "sold" && (
            history.sold_listings.length === 0 ? (
              <div className="h57-surface p-8 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">
                No sold cards yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="history-sold-grid">
                {history.sold_listings.map((s) => (
                  <div key={s.id} className="h57-surface p-2 group" data-testid={`sold-card-${s.id}`}>
                    <div className="relative aspect-[3/4] bg-black/40 overflow-hidden">
                      {s.front_image && (
                        <img
                          src={imgUrl(s.front_image, 480)}
                          alt={s.title || s.player_name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute top-2 left-2 h57-chip !py-1 !px-2 !text-[9px] !border-[#FFD700] !text-[#FFD700] bg-black/60">
                        SOLD
                      </div>
                      {s.sale_price != null && (
                        <div className="absolute bottom-2 right-2 h57-chip !py-1 !px-2 !text-[9px] bg-black/60">
                          ${Number(s.sale_price).toFixed(0)}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 px-1">
                      <div className="font-display font-bold text-sm leading-tight truncate">
                        {s.title || `${s.year || ""} ${s.brand || ""} ${s.player_name || ""}`.trim()}
                      </div>
                      {s.sold_at && (
                        <div className="mt-0.5 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                          Sold {new Date(s.sold_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {historyTab === "feedback" && (
            history.feedback.length === 0 ? (
              <div className="h57-surface p-8 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">
                No feedback yet.
              </div>
            ) : (
              <div className="space-y-3" data-testid="history-feedback-list">
                {history.feedback.map((f) => (
                  <div key={f.id} className="h57-surface p-4" data-testid={`feedback-row-${f.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <StarRow stars={f.stars} size={14} />
                          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                            from @{f.buyer_username}
                          </span>
                        </div>
                        {f.listing_title && (
                          <Link
                            to={`/listing/${f.listing_id}`}
                            className="mt-1 block font-display font-bold text-sm hover:text-[#FF5F1F] transition-colors"
                          >
                            {f.listing_title}
                          </Link>
                        )}
                      </div>
                      <div className="text-right">
                        {f.sale_price != null && (
                          <div className="font-display font-bold text-sm">${Number(f.sale_price).toFixed(0)}</div>
                        )}
                        {f.at && (
                          <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                            {new Date(f.at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          </div>
                        )}
                      </div>
                    </div>
                    {f.comment && (
                      <p className="mt-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{f.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </section>
      )}
    </div>
  );
}
